// No SDK or optional storage is accessed before the first-party consent endpoint permits it.
let context;
let starting = false;
const denied = { analytics: false, marketing: false };
window.tokchartConsent = denied;
const requestContext = async () => {
    const response = await fetch('/analytics/consent', {
        credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' },
        signal: AbortSignal.timeout(4000),
    });
    if (!response.ok) throw new Error('Consent unavailable');
    return response.json();
};
const removeTrackingStorage = ({ analytics = true, marketing = true } = {}) => {
    try {
        for (const key of Object.keys(localStorage)) if (analytics && /^(__ph_opt_in_out_|ph_)/.test(key)) localStorage.removeItem(key);
        for (const key of Object.keys(sessionStorage)) if (analytics && /^(__ph_opt_in_out_|ph_)/.test(key)) sessionStorage.removeItem(key);
    } catch (_) { /* Storage can be unavailable. */ }
    for (const cookie of document.cookie.split(';')) {
        const name = cookie.split('=')[0].trim();
        if (!(analytics && /^(ph_|__ph_opt_in_out_)/.test(name)) && !(marketing && /^(_ga|_fbp$|_fbc$)/.test(name))) continue;
        const domains = [null, location.hostname, '.' + location.hostname];
        const parts = location.hostname.split('.');
        if (parts.length > 2) domains.push('.' + parts.slice(-2).join('.'));
        for (const domain of domains) document.cookie = `${name}=;Max-Age=0;path=/;SameSite=Lax${domain ? ';domain=' + domain : ''}`;
    }
};
const startAnalytics = async () => {
    const config = window.tokchartTrackingConfig;
    if (starting || !config?.enabled || !config.key || !context.analytics || context.excluded) return;
    starting = true;
    // Dynamic imports are served by Vite, so consent does not depend on a third-party request.
    // The bundled recorder registers rrweb because external dependency loading stays disabled.
    const [{ default: posthog }] = await Promise.all([import('posthog-js'), import('posthog-js/dist/posthog-recorder')]);
    if (!window.tokchartConsent.analytics) return;
    window.posthog = posthog;
    posthog.init(config.key, {
        api_host: config.host, ui_host: config.uiHost,
        persistence: 'cookie', cookie_expiration: 90, cross_subdomain_cookie: false,
        person_profiles: 'identified_only', autocapture: false, capture_pageview: false,
        capture_pageleave: false, capture_exceptions: false, capture_performance: false,
        disable_session_recording: false, session_recording: { maskAllInputs: true, maskTextSelector: null },
        disable_surveys: true, advanced_disable_feature_flags: true,
        enable_heatmaps: false, rageclick: false, capture_dead_clicks: false,
        enable_recording_console_log: false, disable_external_dependency_loading: true,
        ip: false, save_referrer: false, save_campaign_params: false,
        before_send(event) {
            if (!window.tokchartConsent.analytics) return null;
            // Replay snapshots need $snapshot_data, so they skip the property allowlist.
            if (event.event === '$snapshot') {
                // Meta and custom replay events carry the page href, which can contain query strings.
                const stripQuery = href => typeof href === 'string' ? href.split(/[?#]/)[0] : href;
                for (const item of event.properties?.$snapshot_data || []) {
                    if (item?.data?.href) item.data.href = stripQuery(item.data.href);
                    if (item?.data?.payload?.href) item.data.payload.href = stripQuery(item.data.payload.href);
                }
                event.properties = { ...event.properties, $ip: null, $geoip_disable: true };
                return event;
            }
            if (!['$pageview', '$identify'].includes(event.event)) return null;
            // The SDK's default properties can contain URL query strings and campaign IDs.
            // The public project token is mandatory for SDK ingestion.
            const keep = new Set(['token', 'distinct_id', '$device_id', '$user_id', '$anon_distinct_id', '$is_identified', '$lib', '$lib_version', '$session_id', '$window_id', '$host', '$pathname', '$process_person_profile', '$set', '$set_once']);
            event.properties = Object.fromEntries(Object.entries(event.properties || {}).filter(([key]) => keep.has(key)));
            event.properties.$ip = null;
            event.properties.$geoip_disable = true;
            delete event.properties.$set;
            delete event.properties.$set_once;
            return event;
        },
        loaded(ph) {
            if (context.distinct_id) ph.identify(context.distinct_id);
            else if (String(ph.get_distinct_id()).startsWith('user:')) ph.reset();
            ph.capture('$pageview', { $pathname: location.pathname });
            window.dispatchEvent(new Event('tokchart:analytics-ready'));
        },
    });
};
const apply = () => {
    const gpc = navigator.globalPrivacyControl === true;
    window.tokchartConsent = {
        analytics: !!context?.analytics && !gpc && !context?.excluded,
        marketing: !!context?.marketing && !gpc && !context?.excluded,
    };
    if (!window.tokchartConsent.analytics) {
        window.posthog?.opt_out_capturing();
        removeTrackingStorage({ analytics: true, marketing: !window.tokchartConsent.marketing });
    } else startAnalytics().catch(() => {});
};
const init = async () => {
    const panel = document.getElementById('cookie-choices');
    if (!panel) return;
    const analytics = document.getElementById('consent-analytics');
    const marketing = document.getElementById('consent-marketing');
    const error = document.getElementById('consent-error');
    const open = () => {
        analytics.checked = !!context?.analytics;
        marketing.checked = !!context?.marketing;
        panel.hidden = false;
        panel.querySelector('button').focus();
    };
    document.querySelectorAll('[data-cookie-settings]').forEach(button => button.addEventListener('click', open));
    panel.querySelectorAll('[data-consent]').forEach(button => button.addEventListener('click', async () => {
        const choice = button.dataset.consent;
        const body = choice === 'save' ? { analytics: analytics.checked, marketing: marketing.checked }
            : { analytics: choice === 'accept', marketing: choice === 'accept' };
        if (!body.analytics) { window.tokchartConsent = denied; window.posthog?.opt_out_capturing(); }
        error.hidden = true;
        panel.querySelectorAll('button').forEach(b => b.disabled = true);
        try {
            context = await requestContext();
            const response = await fetch('/analytics/consent', {
                method: 'POST', credentials: 'same-origin', cache: 'no-store',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-TOKEN': context.csrf_token },
                body: JSON.stringify(body), signal: AbortSignal.timeout(4000),
            });
            if (!response.ok) throw new Error('Save failed');
            removeTrackingStorage({ analytics: true, marketing: !body.marketing });
            // Reload establishes one consistent consent/identity state before rendering experiments.
            location.reload();
        } catch (_) { error.hidden = false; }
        finally { panel.querySelectorAll('button').forEach(b => b.disabled = false); }
    }));
    try {
        context = await requestContext();
        // GPC also revokes previous server-side permission, not just this page's SDK.
        if (navigator.globalPrivacyControl === true) {
            await fetch('/analytics/consent', { method: 'POST', credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': context.csrf_token },
                body: JSON.stringify(denied), signal: AbortSignal.timeout(4000) });
            context = { ...context, ...denied, chosen: true };
        }
        apply();
        startAdvertising();
        if (!context.chosen) open();
    } catch (_) { open(); }
};
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();

// AdSense additionally requires the advertising CMP's vendor/purpose permission.
// First-party attribution consent alone never grants permission to a vendor.
function startAdvertising() {
    if (!window.tokchartConsent.marketing || !window.tokchartAdsEligible) return;
    let attempts = 0;
    const connect = () => {
        if (!window.tokchartConsent.marketing) return;
        if (typeof window.__tcfapi !== 'function') {
            if (++attempts < 40) setTimeout(connect, 250);
            return;
        }
        window.__tcfapi('addEventListener', 2, (tc, success) => {
            if (!success || !window.tokchartConsent.marketing || navigator.globalPrivacyControl === true) return;
            if (!['tcloaded', 'useractioncomplete'].includes(tc.eventStatus)) return;
            const permitted = tc.gdprApplies === false || (tc.gdprApplies === true && !!tc.tcString
                && tc.vendor?.consents?.[755]
                && [1, 3, 4].every(id => tc.purpose?.consents?.[id])
                && [2, 7, 9, 10].every(id => tc.purpose?.consents?.[id] || (tc.vendor?.legitimateInterests?.[755] && tc.purpose?.legitimateInterests?.[id])));
            if (!permitted) {
                window.gtag?.('consent', 'update', { ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });
                if (document.getElementById('tokchart-adsense')) location.reload();
                return;
            }
            if (document.getElementById('tokchart-adsense')) return;
            window.gtag?.('consent', 'update', { ad_storage: 'granted', ad_user_data: 'granted', ad_personalization: 'granted' });
            const script = document.createElement('script');
            script.id = 'tokchart-adsense'; script.async = true; script.crossOrigin = 'anonymous';
            script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-8416999256256451';
            document.head.appendChild(script);
        });
    };
    connect();
}
